/**
 * Event Builder — payload builders (plan 3.3 / 3.4 rule 3 / Appendix A).
 *
 * Each builder returns exactly the body the corresponding old wizard page posts today:
 *   toTypePayload         ≙ add-new-event.jsx onSubmit / onSaveAsDraft
 *   toDetailsPayload      ≙ add-new-event-details.jsx (free & paid shapes, payments mapping 6.H)
 *   toAvailabilityPayload ≙ add-new-event-availbility.jsx (+ ported processScheduleForSave)
 *   toAdvancedPayload     ≙ add-new-event-advanced.jsx
 *
 * Rules honoured here (3.4): full payloads only (rule 3); `keep_draft` (rule 4); image only
 * when a File is present (rule 9); locked flags are never sent as `E` (8.2 / rule 11);
 * `normalizeStaffAssignmentPayload` is applied exactly as today (D16).
 */

import { getAdminBusinessId } from "../../../../utils/authHelper";
import { normalizeStaffAssignmentPayload, hasPooledAvailability } from "../../../../utils/organizationStaffAccess";
import { FEATURE_IDS } from "../../../../constants/featureAccess";
import {
  ASSIGNMENT_MODE,
  CONFIRMATION_PAGE,
  D,
  E,
  FLAT_PERCENT,
  FORMAT,
  KIND,
  PAYMENT_TYPE,
  SCHEDULE_TYPE,
  STAFF_ASSIGNMENT_TYPE,
  STAFF_CHOOSE,
  VENUE,
} from "./constants";

// ---- Feature helpers (single source; validation.js re-exports these) ------------------------------

export const featureEnabled = (features, featureId) =>
  (features || []).some((f) => Number(f.feature_id) === Number(featureId) && f.status === E);

const str = (v) => (v === null || v === undefined ? "" : String(v));
const intOrZero = (v) => parseInt(v, 10) || 0;

// ---- Type page -----------------------------------------------------------------------------------------

/**
 * @param {object} type      state.type
 * @param {object} meta      state.meta ({ eventId, kind })
 * @returns {object} body for POST admin/event/save-event-type-page
 */
export function toTypePayload(type, meta) {
  const venueId = parseInt(type.venue_id, 10);
  const isGroup = String(type.event_format_id) === FORMAT.GROUP;

  let body = {
    event_name: type.event_name,
    no_of_attendies: isGroup ? intOrZero(type.no_of_attendies) : 1,
    event_format_id: String(type.event_format_id),
    venue_id: str(type.venue_id),
    event_url: type.event_url,
    is_event_paid: meta.kind === KIND.PAID ? E : D,
    business_id: getAdminBusinessId(),
  };

  if (venueId === VENUE.IN_PERSON) {
    body = {
      ...body,
      venue_location: str(type.venue_location),
      venue_location_notes: str(type.venue_location_notes),
      venue_location_status: str(type.venue_location_status),
    };
  }
  if (venueId === VENUE.PHONE) {
    body = {
      ...body,
      invitee_call_status: str(type.invitee_call_status),
      host_phone_no: str(type.host_phone_no),
    };
  }
  if (venueId === VENUE.OTHER) {
    body = {
      ...body,
      venue_other_notes: str(type.venue_other_notes),
      venue_other_display_status: str(type.venue_other_display_status),
    };
  }

  if (meta.eventId) {
    body.id = meta.eventId; // saveEventType adds `id` for edits; server uses $request->has('id')
  }
  return body;
}

// ---- Details page ------------------------------------------------------------------------------------

/** Same semantics as add-new-event-details.jsx `sanitizeLineItems`. */
export const sanitizeLineItems = (items = [], defaultQty = 1) =>
  (items || [])
    .filter((item) => item && String(item.item_name || "").trim() !== "")
    .map((item) => ({
      item_name: String(item.item_name).trim(),
      price: item.price === "" || item.price === null || item.price === undefined ? 0 : item.price,
      qty: item.qty || defaultQty,
    }));

/**
 * Prepayment mapping table (plan 6.H — the only place the `payments[]` shape is decided).
 * Mirrors the old page: first row = payments_type (full|partial), deposit row appended when on.
 * Pay at event (`event_prepaid_status D`): rows are not stored by the server; we still send a
 * full_payment row so the array is never empty.
 */
export function buildPaymentsRows(details) {
  const prepaid = details.event_prepaid_status === E;
  const first = {
    payment_type: prepaid && details.payments_type ? details.payments_type : PAYMENT_TYPE.FULL,
    payment_type_flat_percent: details.payments_flat_type || FLAT_PERCENT.PERCENT,
    price:
      prepaid && details.payments_type === PAYMENT_TYPE.PARTIAL ? str(details.payments_price) : "",
  };
  const rows = [first];
  if (prepaid && details.deposit_type === PAYMENT_TYPE.DEPOSIT && str(details.deposit_price) !== "") {
    rows.push({
      payment_type: PAYMENT_TYPE.DEPOSIT,
      payment_type_flat_percent: details.deposit_flat_per_type || FLAT_PERCENT.PERCENT,
      price: str(details.deposit_price),
    });
  }
  return rows;
}

/**
 * @param {object} details   state.details
 * @param {object} meta      state.meta
 * @param {Array}  features  redux featureAccess.features (rule 11 stripping)
 * @returns {object} plain object; the engine passes it through objectToFormData (as saveEventDetails does)
 */
export function toDetailsPayload(details, meta, features) {
  const isPaid = meta.kind === KIND.PAID;

  const base = {
    event_color: str(details.event_color),
    event_description: str(details.event_description),
    max_no_of_booking: intOrZero(details.max_no_of_booking),
    max_no_booking_status: details.max_no_booking_status || D,
    recurring_booking_status: details.recurring_booking_status || D,
    allow_rescheduling_status: details.allow_rescheduling_status || D,
    attendee_cancellation_status: details.attendee_cancellation_status || D,
    cancellation_policy: str(details.cancellation_policy),
    ita_booking_page_status: details.ita_booking_page_status || D,
    ita_confirmation_page_status: details.ita_confirmation_page_status || D,
    ita_reminder_email_status: details.ita_reminder_email_status || D,
    instruction_to_attendee: str(details.instruction_to_attendee),
    // today's free initialValues carry these too (server ignores them for free events)
    refund_type: str(details.refund_type),
    refund_percentage: str(details.refund_percentage),
    refund_cutoff_time: str(details.refund_cutoff_time),
    id: meta.eventId,
  };

  // rule 9: only send a real File; `objectToFormData` would otherwise append "undefined"/"null"
  if (details.event_image instanceof File) {
    base.event_image = details.event_image;
  }

  if (!isPaid) {
    return base;
  }

  const hasCoupons = featureEnabled(features, FEATURE_IDS.COUPON_CODES);
  const hasAddOns = featureEnabled(features, FEATURE_IDS.UPSELL_CROSS_SELL);

  return {
    ...base,
    coupon_codes_status: hasCoupons ? details.coupon_codes_status || D : D, // rule 11 (server 403s on E without feature 10)
    recurrent_booking_discounts_status: details.recurrent_booking_discounts_status || D,
    staff_commission_status: details.staff_commission_status || D,
    staff_commission_type: details.staff_commission_type === FLAT_PERCENT.FLAT ? FLAT_PERCENT.FLAT : FLAT_PERCENT.PERCENT,
    staff_commission_rate:
      details.staff_commission_type === FLAT_PERCENT.FLAT
        ? Number(details.staff_commission_rate) || 0
        : intOrZero(details.staff_commission_rate),
    event_prepaid_status: details.event_prepaid_status || D,
    pricing: sanitizeLineItems(details.pricing, 1),
    // rule 11 (server 403s on any named add-on without feature 12). Today's sanitizeLineItems sends
    // `qty || 1` for add-ons as well (their starter row has qty 0) — kept byte-identical.
    add_ons: hasAddOns ? sanitizeLineItems(details.add_ons, 1) : [],
    payments: buildPaymentsRows(details),
  };
}

// ---- Availability page -------------------------------------------------------------------------------

/**
 * Ported verbatim from add-new-event-availbility.jsx `processScheduleForSave` — including the
 * `schedule_availibility` key spelling the server reads (AvailabilityTrait::saveEventAvailability).
 * Only addition: intervals with an empty start or end are dropped (validation already blocks them;
 * this keeps a stray blank row from making Carbon::parse('') throw a 422).
 */
export function processScheduleForSave(schedules, eventId, eventTimezoneId) {
  const businessId = getAdminBusinessId();
  // the event's timezone select is the single source of truth (availability.timezone_id);
  // staff rows keep their own timezone when they have one (staff template), else the event's
  const tzEvent = (row) => (eventTimezoneId !== undefined && eventTimezoneId !== "" ? eventTimezoneId : row.timezoneId);
  const tzStaff = (row) => row.timezoneId || (eventTimezoneId !== undefined ? eventTimezoneId : "");
  const staffs = [];
  const processedSchedules = [];

  (schedules || []).forEach((availability) => {
    const scheduleDetails = [];

    (availability.schedules || []).forEach((scheduleDetail) => {
      const weekDayId = Object.prototype.hasOwnProperty.call(scheduleDetail, "dayId") ? scheduleDetail.dayId : null;
      const hasDated = Object.prototype.hasOwnProperty.call(scheduleDetail, "dated") && scheduleDetail.dated !== null;
      const dated = hasDated ? scheduleDetail.dated : null;
      const type = hasDated ? "custom" : "weekly";

      scheduleDetails.push({
        weekday_id: weekDayId,
        dated,
        on_status: E,
        type,
        schedule_availibility: (scheduleDetail.schedules || [])
          .filter((s) => s && str(s.start_time) !== "" && str(s.end_time) !== "")
          .map((s) => ({ start_time: s.start_time, end_time: s.end_time })),
      });
    });

    if (availability.type === SCHEDULE_TYPE.EVENT_STAFF) {
      staffs.push({
        event_id: eventId,
        provider_id: 1,
        timezone_id: tzStaff(availability),
        schedule_status: E,
        business_id: businessId,
        staff_id: availability.staffId,
        schedule_details: scheduleDetails,
      });
    } else if (availability.type === SCHEDULE_TYPE.EVENT) {
      processedSchedules.push({
        schedule_type: SCHEDULE_TYPE.EVENT,
        event_id: eventId,
        provider_id: 1,
        timezone_id: tzEvent(availability),
        schedule_status: E,
        business_id: businessId,
        schedule_details: scheduleDetails,
      });
    }
  });

  if (staffs.length > 0) {
    processedSchedules.push({ schedule_type: SCHEDULE_TYPE.EVENT_STAFF, staffs });
  }
  return processedSchedules;
}

/**
 * D16: assignmentMode → { staff_assignment_type, staff_choose, staff_assignment_method, staffs }.
 * Pooled-lost fallback (plan 6.E edge cases): an `auto` event whose plan lost feature 4 is sent as
 * `invitee` (staff kept) so the server never sees `staff_choose ''` with type 2 (a 422).
 */
export function assignmentFields(availability, features) {
  const staffs = (availability.staffs || []).map((id) => parseInt(id, 10)).filter((n) => !Number.isNaN(n));
  let mode = availability.assignmentMode || ASSIGNMENT_MODE.MY;

  if (mode === ASSIGNMENT_MODE.AUTO && !hasPooledAvailability(features)) {
    mode = staffs.length ? ASSIGNMENT_MODE.INVITEE : ASSIGNMENT_MODE.MY;
  }
  if (mode !== ASSIGNMENT_MODE.MY && staffs.length === 0) {
    mode = ASSIGNMENT_MODE.MY; // server: type 2 requires staffs min:1
  }

  switch (mode) {
    case ASSIGNMENT_MODE.ASSIGN:
      return {
        staff_assignment_type: STAFF_ASSIGNMENT_TYPE.STAFF,
        staff_choose: STAFF_CHOOSE.ALLOW_INVITEE,
        staff_assignment_method: "",
        staffs: staffs.slice(0, 1),
      };
    case ASSIGNMENT_MODE.INVITEE:
      return {
        staff_assignment_type: STAFF_ASSIGNMENT_TYPE.STAFF,
        staff_choose: STAFF_CHOOSE.ALLOW_INVITEE,
        staff_assignment_method: "",
        staffs,
      };
    case ASSIGNMENT_MODE.AUTO:
      return {
        staff_assignment_type: STAFF_ASSIGNMENT_TYPE.STAFF,
        staff_choose: STAFF_CHOOSE.RANDOM,
        staff_assignment_method: availability.staff_assignment_method || "",
        staffs,
      };
    case ASSIGNMENT_MODE.MY:
    default:
      return {
        staff_assignment_type: STAFF_ASSIGNMENT_TYPE.MY_EVENT,
        staff_choose: "",
        staff_assignment_method: "",
        staffs: [],
      };
  }
}

/**
 * @param {object} availability state.availability
 * @param {object} meta         state.meta
 * @param {object} userPlan     redux subscription.userPlan
 * @param {Array}  features     redux featureAccess.features
 */
export function toAvailabilityPayload(availability, meta, userPlan, features) {
  const assignment = assignmentFields(availability, features);

  // Staff whose assignment was dropped must not keep their per-staff hours rows
  const keptStaffIds = new Set(assignment.staffs.map(String));
  const schedules = (availability.schedules || []).filter(
    (row) => row.type !== SCHEDULE_TYPE.EVENT_STAFF || keptStaffIds.has(String(row.staffId))
  );

  const isDateRange = availability.event_validity === "date_range";
  const isValidOver = availability.event_validity === "valid_over";

  const body = {
    event_duration_in_minutes: intOrZero(availability.event_duration_in_minutes),
    invitee_set_duration_status: availability.invitee_set_duration_status || D,
    max_event_per_week: intOrZero(availability.max_event_per_week),
    max_event_per_day: intOrZero(availability.max_event_per_day),
    buffer_before_event_minutes: intOrZero(availability.buffer_before_event_minutes),
    max_scheduling_notice_minutes: intOrZero(availability.max_scheduling_notice_minutes),
    min_scheduling_notice_minutes: intOrZero(availability.min_scheduling_notice_minutes),
    buffer_after_event_minutes: intOrZero(availability.buffer_after_event_minutes),
    slot_duration_increment_of: intOrZero(availability.slot_duration_increment_of),
    ...assignment,
    event_validity: availability.event_validity || "indefinitly",
    valid_over_period: isValidOver ? str(availability.valid_over_period) : "",
    is_time_block: D, // old page: "Time block UI removed — always persist as disabled"
    timezone_id: str(availability.timezone_id), // rule 14 (today sends '')
    event_start_date: isDateRange ? str(availability.event_start_date) : "",
    event_end_date: isDateRange ? str(availability.event_end_date) : "",
    event_internal_notes: str(availability.event_internal_notes),
    schedules: processScheduleForSave(schedules, meta.eventId, str(availability.timezone_id)),
    id: meta.eventId,
  };

  return normalizeStaffAssignmentPayload(body, userPlan, features);
}

// ---- Advanced page -----------------------------------------------------------------------------------

/** Today's `formatLabel` — option value = label with spaces → '-' and lower-cased. */
export const optionValueFromLabel = (label) => String(label || "").replace(/ /g, "-").toLowerCase();

export function cleanQuestions(questions) {
  return (questions || [])
    .filter((q) => q && String(q.question || "").trim() !== "")
    .map((q) => ({
      question: q.question,
      question_type: q.question_type,
      show_status: q.show_status === E ? E : D,
      mandatory_status: q.mandatory_status === E ? E : D,
      details: (q.details || [])
        .filter((d) => d && String(d.field_label || "").trim() !== "")
        .map((d) => ({
          field_label: d.field_label,
          // both keys mandatory: the server inserts $questionDetail['field_value'] directly
          field_value: d.field_value && String(d.field_value) !== "" ? d.field_value : optionValueFromLabel(d.field_label),
        })),
    }));
}

/**
 * @param {object} advanced   state.advanced
 * @param {object} meta       state.meta
 * @param {object} lookups    state.lookups ({ orgStaffCanConfirm })
 * @param {Array}  features   redux featureAccess.features
 * @param {boolean} smsAllowed utils/trialAccess.isSmsAllowed(...)
 * @param {object} opts       { keepDraft: boolean } — rule 4: 'E' while the event is a draft, absent on publish
 */
export function toAdvancedPayload(advanced, meta, lookups, features, smsAllowed, { keepDraft = false } = {}) {
  const has = (id) => featureEnabled(features, id);

  const external = advanced.confirmation_page === CONFIRMATION_PAGE.EXTERNAL && has(FEATURE_IDS.REDIRECT_CUSTOM_PAGES);
  const confirmationPage = external ? CONFIRMATION_PAGE.EXTERNAL : CONFIRMATION_PAGE.DEFAULT;

  const body = {
    event_public_visiblity_status: advanced.event_public_visiblity_status || D,
    confirmation_page: confirmationPage,
    confirmation_page_url: external ? str(advanced.confirmation_page_url) : "",
    customer_feedback_status: advanced.customer_feedback_status || D,
    email_reminder_status: has(FEATURE_IDS.AUTOMATED_REMINDERS) ? advanced.email_reminder_status || D : D,
    sms_reminder_status:
      has(FEATURE_IDS.AUTOMATED_REMINDERS) && smsAllowed ? advanced.sms_reminder_status || D : D,
    email_followup_status: has(FEATURE_IDS.AUTOMATED_EVENT_NOTIFICATIONS) ? advanced.email_followup_status || D : D,
    invitee_guests_status: has(FEATURE_IDS.GUESTS) ? advanced.invitee_guests_status || D : D,
    // old page: orgStaffCanConfirm === 'E' && value === 'E' ? 'E' : 'D'; plus feature 23 (server 403s otherwise)
    staff_can_confirm_booking:
      has(FEATURE_IDS.STAFF_CONFIRMATION) && lookups.orgStaffCanConfirm === E && advanced.staff_can_confirm_booking === E
        ? E
        : D,
    event_questions: cleanQuestions(advanced.questions),
    id: meta.eventId,
  };

  if (keepDraft) {
    body.keep_draft = E; // backend 7.1
  }
  return body;
}
